import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Row, Col, Table, Dropdown, Menu } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';

import OnTraccrButton from '../../../common/buttons/OnTraccrButton';
import OnTraccrTextInput from '../../../common/inputs/OnTraccrTextInput';
import TableDeleteButton from './TableDeleteButton';
import { getIdMap } from '../../../helpers/helpers';
import DisplayText from '../../../common/text/DisplayText';

import CustomSavedTableAddDrawer from './CustomSavedTableAddDrawer';
import { generateId, getHasColumnPermissions } from '../../formHelpers';
import Permissions from '../../../auth/Permissions';
import useCheckTableMaxRows from '../../../common/hooks/useCheckTableMaxRows';
import { calculationTableRender, getCustomTableColumn, LINE_NUMBER_COLUMN } from './formFieldsHelpers';
import { pendingTableColumn } from '../../forms.constants';

export default function CustomSavedFormTablePreview({
  title = null,
  columns = [],
  previewProps = {},
  setPreviewProps,
  isDisplay,
  id,
  setResponses,
  responses = {},
  responding = false,
  configProps,
  customTables = {},
  showCondensedView,
}) {
  const {
    dataType,
    fieldDataType,
    requiredColumns,
    hideAddNewButton,
    preventEdits,
    columnPermissions = {},
  } = configProps;
  
  const positionNames = useSelector((state) => state.settings.positionNames);
  const users = useSelector((state) => state.users.users);
  const positionNameMap = useMemo(() => getIdMap(positionNames, 'name'), [positionNames]);
  const userMap = useMemo(() => getIdMap(users), [users]);
  const userPositionId = useMemo(() => {
    const userPosition = userMap[Permissions.id]?.position;
    return positionNameMap[userPosition]?.id;
  }, [positionNameMap, userMap]);

  const relevantDataType = fieldDataType ?? dataType;
  const {
    [relevantDataType]: {
      options = [],
      identifier,
    } = {},
  } = customTables;
  const values = previewProps.values || []; // For Responses
  const {
    selected: previewSelected = [],
  } = previewProps;
  const {
    [id]: {
      values: responseSelected = [],
    } = {},
  } = responses;

  const selected = responding ? responseSelected : previewSelected;

  const [showAdd, setShowAdd] = useState(false);

  const showAddDrawer = useCallback(() => setShowAdd(true), []);
  const closeAddDrawer = useCallback(() => setShowAdd(false), []);

  const optionMap = useMemo(() => getIdMap(options), [options]);

  const onAddNew = useCallback(() => {
    const newSelected = selected.concat([{
      id: generateId(),
      isCustom: true,
    }]);
    if (responding) {
      setResponses({
        ...responses,
        [id]: {
          ...(responses[id]),
          values: newSelected,
          columns,
        },
      });
    } else {
      setPreviewProps({
        ...previewProps,
        selected: newSelected,
      });
    }
  }, [responding, selected, previewProps, responses]);

  const onSelect = useCallback((optionIds) => {
    const newSelected = selected.concat(
      optionIds.map((optionId) => {
        const {
          [optionId]: selectedOption = {},
        } = optionMap;
        return {
          ...selectedOption,
          id: `${optionId}.${DateTime.local().toMillis()}`, // unique ID because same row can appear multiple times
        };
      }),
    );
    if (responding) {
      setResponses({
        ...responses,
        [id]: {
          ...(responses[id]),
          values: newSelected,
          columns,
        },
      });
    } else {
      setPreviewProps({
        ...previewProps,
        selected: newSelected,
      });
    }
    setShowAdd(false);
  }, [id, responding, responses, previewProps, selected, optionMap]);

  const onDelete = useCallback((itemId) => {
    const newSelected = selected.filter((item) => item.id !== itemId);
    if (responding) {
      setResponses({
        ...responses,
        [id]: {
          ...(responses[id]),
          values: newSelected,
          columns,
        },
      });
    } else {
      setPreviewProps({
        ...previewProps,
        selected: newSelected,
      });
    }
  }, [id, responses, previewProps, selected, columns, responding, setResponses, setPreviewProps]);

  const onCellValueChanged = useCallback((itemId, key, newValue) => {
    const newSelected = selected.map((item) => {
      if (item.id !== itemId) return item;
      return {
        ...item,
        [key]: newValue,
      };
    });
    if (responding) {
      setResponses({
        ...responses,
        [id]: {
          ...(responses[id]),
          values: newSelected,
          columns,
        },
      });
    } else {
      setPreviewProps({
        ...previewProps,
        selected: newSelected,
      });
    }
  }, [id, responses, previewProps, selected, columns, responding, setResponses, setPreviewProps]);

  const onValueChanged = useCallback(({ id: itemId, key }) => (e) => {
    const {
      target: {
        value: newValue,
      } = {},
    } = e;
    const newSelected = selected.map((item) => {
      if (item.id !== itemId) return item;
      return {
        ...item,
        [key]: newValue,
      };
    });
    if (responding) {
      setResponses({
        ...responses,
        [id]: {
          ...(responses[id]),
          values: newSelected,
          columns,
        },
      });
    } else {
      setPreviewProps({
        ...previewProps,
        selected: newSelected,
      });
    }
  }, [id, responses, previewProps, selected]);

  const dataSource = useMemo(() => (
    isDisplay && !responding ? values : selected
  ), [isDisplay, responding, values, selected]);

  const hasPendingTableData = useMemo(
    () => dataSource.some((row) => row.pendingTableValue),
    [dataSource],
  );

  const tableColumns = useMemo(() => {
    const colWidth = columns.length > 0
      ? { maxWidth: `${75 / columns.length}vw` }
      : {};
    const cols = columns.flatMap((col) => {
      if (col.isCustomText) {
        if (!getHasColumnPermissions({
          columnPermissions,
          column: col,
          userPositionId,
          userId: Permissions.id,
        })) {
          return [];
        }
        return [getCustomTableColumn(col, {
          isDisplay,
          preventEdits,
          requiredColumns,
          responses,
          onChange: (record, value) => onCellValueChanged(record.id, col.key, value),
        })];
      }
      return [{
        title: <div className={requiredColumns && 'form-required-field'}>{col.key}</div>,
        dataIndex: col.key,
        render: (value, item) => {
          if (col.isCalculation) {
            return calculationTableRender(col, isDisplay)(value);
          }
          return (
            isDisplay || preventEdits ? (
              <DisplayText
                title={value}
                style={{
                  width: 'fit-content',
                  wordBreak: 'break-word',
                  wordWrap: 'pre-wrap',
                  fontSize: 14,
                  display: 'table-cell',
                  verticalAlign: 'middle',
                  fontFamily: 'roboto-regular',
                  color: 'rbga(0, 0, 0, 0.65)',
                  ...colWidth,
                }}
              />
            ) : (
              <OnTraccrTextInput
                key={col.key + item.id}
                textarea
                value={value}
                onChange={onValueChanged({ id: item.id, key: col.key })}
                autoSize={isDisplay}
              />
            )
          );
        },
      }];
    });
    if (!isDisplay) {
      cols.push({
        title: '',
        dataIndex: '',
        width: 100,
        render: (_, record) => (
          <TableDeleteButton onConfirm={() => onDelete(record.id)} />
        ),
      });
    }
    if (hasPendingTableData) {
      cols.unshift(pendingTableColumn);
    }
    cols.unshift(LINE_NUMBER_COLUMN);
    return cols;
  }, [
    isDisplay,
    columns,
    onDelete,
    requiredColumns,
    preventEdits,
    hasPendingTableData,
    onCellValueChanged,
    onValueChanged,
    columnPermissions,
    userPositionId,
    responses,
  ]);

  const {
    maxExistingAllowed,
    shouldAddButtonBeEnabled = true,
  } = useCheckTableMaxRows({
    configProps,
    currentRowsLength: dataSource?.length,
  });

  return (
    <>
      <Row style={{ marginTop: showCondensedView ? 0 : 15 }}>
        <Row justify="space-between" style={{ width: '100%', marginBottom: 10 }}>
          <Col>
            {title}
          </Col>
          {!isDisplay && (
            <Dropdown
              placement="bottomRight"
              trigger={['click']}
              overlay={(
                <Menu
                  onClick={({ key }) => {
                    switch (key) {
                      case 'addOption':
                        showAddDrawer();
                        break;
                      case 'addNew':
                        onAddNew();
                        break;
                    }
                  }}
                >
                  {!hideAddNewButton && (
                    <Menu.Item key="addNew" disabled={!shouldAddButtonBeEnabled}>
                      Add Row
                    </Menu.Item>
                  )}
                  <Menu.Item key="addOption" disabled={!shouldAddButtonBeEnabled}>
                    Add Option
                  </Menu.Item>
                </Menu>
              )}
            >
              <OnTraccrButton
                title="Add"
                icon={<DownOutlined />}
                disabled={!shouldAddButtonBeEnabled}
              />
            </Dropdown>
          )}
        </Row>
        { !showCondensedView || dataSource?.length ? (
          <Table
            className='form-table-preview'
            style={{ width: '100%', overflow: 'auto' }}
            columns={tableColumns}
            size="small"
            pagination={false}
            dataSource={dataSource}
            tableLayout="auto"
            rowClassName={(record) => (record.pendingTableValue ? 'pending-table-value' : '')}
          />
        ) : (
          <DisplayText title="No Items Selected" style={{ marginBottom: 0 }} />
        )}
      </Row>
      <CustomSavedTableAddDrawer
        options={options}
        identifier={identifier}
        visible={showAdd}
        onClose={closeAddDrawer}
        onSubmit={onSelect}
        maxExistingAllowed={maxExistingAllowed}
      />
    </>
  );
}

CustomSavedFormTablePreview.propTypes = {
  title: PropTypes.node,
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    title: PropTypes.string,
  })),
  previewProps: PropTypes.shape({
    values: PropTypes.arrayOf(PropTypes.shape({})),
    selected: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  setPreviewProps: PropTypes.func,
  isDisplay: PropTypes.bool,
  id: PropTypes.string,
  setResponses: PropTypes.func,
  responses: PropTypes.shape({}),
  responding: PropTypes.bool,
  configProps: PropTypes.shape({
    dataType: PropTypes.string,
    fieldDataType: PropTypes.string,
    requiredColumns: PropTypes.bool,
    hideAddNewButton: PropTypes.bool,
    preventEdits: PropTypes.bool,
  }),
  customTables: PropTypes.shape({}),
  showCondensedView: PropTypes.bool,
};

CustomSavedFormTablePreview.defaultProps = {
  title: null,
  columns: [],
  previewProps: {},
  setPreviewProps: null,
  isDisplay: false,
  id: null,
  setResponses: null,
  responses: {},
  responding: false,
  configProps: null,
  customTables: {},
  showCondensedView: false,
};
